if (!customElements.get('mega-menu-sidebar')) {
    /**
    * MegaMenuSidebar Class - V2.0 (Hybrid Logic)
    * Handles tab-like interactions for complex multi-level menus.
    * - Supports "simple" layouts (L2 tabs -> L2 content).
    * - Supports "complex" layouts with a hybrid structure:
    * - L2 items can be dropdowns for L3 sub-tabs.
    * - L2 items can also be direct tabs themselves.
    */
    class MegaMenuSidebar extends HTMLElement {
    constructor() {
    super();
    this.isSimpleLayout = this.classList.contains('menu-layout--simple');
    this.contentPanes = this.querySelectorAll('.mega-menu-content-pane');
    this.heightTransitionFrame = null;
    this.heightTransitionToken = 0;
    }

    connectedCallback() {
    // Use a small timeout to ensure all DOM elements are ready
    setTimeout(() => {
    if (this.isSimpleLayout) {
    this.initSimpleMenu();
    } else {
    this.initComplexMenu();
    }
    }, 100);
    }

    /**
    * Initializes a simple L2 tab menu (e.g., Baby Monitor).
    */
    initSimpleMenu() {
    const tabLinks = this.querySelectorAll('.l2-link-simple');
    tabLinks.forEach(link => {
    link.addEventListener('click', () => this.activateTab(link, tabLinks));
    });
    }

    /**
    * Initializes a complex hybrid menu (e.g., Security Cameras).
    */
    initComplexMenu() {
    const l2Tabs = this.querySelectorAll('.l2-summary--tab');
    const l3Links = this.querySelectorAll('.l3-link');
    const allL2Containers = this.querySelectorAll('.l2-summary, .l2-summary--tab');
    const l2Details = this.querySelectorAll('.l2-menu-item details');
    const sidebar = this.querySelector('.mega-menu-sidebar--sidebar');
    const l2Menu = this.querySelector('.l2-menu-list');
    const allProducts = this.querySelector('.mega-menu-sidebar__all-products');
    let suppressL3HoverUntilPointerMoves = false;
    let openPointerPosition = null;
    let lastPointerPosition = null;

    this.addEventListener('pointermove', event => {
    lastPointerPosition = { x: event.clientX, y: event.clientY };
    if (!suppressL3HoverUntilPointerMoves) {
    return;
    }

    if (!openPointerPosition) {
    suppressL3HoverUntilPointerMoves = false;
    return;
    }

    const movedX = Math.abs(event.clientX - openPointerPosition.x);
    const movedY = Math.abs(event.clientY - openPointerPosition.y);
    if (movedX > 2 || movedY > 2) {
    suppressL3HoverUntilPointerMoves = false;
    openPointerPosition = null;
    }
    });

    const closeOtherL2Details = currentDetails => {
    l2Details.forEach(details => {
    if (details !== currentDetails) {
    details.open = false;
    }
    });
    };

    const activateFirstL3 = summary => {
    this.deactivateAll(allL2Containers, l3Links);
    summary.classList.add('active');
    const firstL3 = summary.closest('details').querySelector('.l3-link');
    if (firstL3) {
    firstL3.classList.add('active');
    this.activatePane(firstL3.dataset.target);
    }
    };

    // Only the left menu controls this popup's height. The right product pane
    // scrolls within it, so its images/content cannot move All Products later.
    const getSidebarHeight = targetOpenDetails => {
    if (!sidebar || !l2Menu || !allProducts) {
    return Math.ceil(this.getBoundingClientRect().height);
    }
    const sourceDetails = Array.from(l2Details);
    const measurementSidebar = sidebar.cloneNode(true);
    const sidebarWidth = sidebar.getBoundingClientRect().width;

    measurementSidebar.setAttribute('aria-hidden', 'true');
    measurementSidebar.classList.add('is-height-measurement');
    measurementSidebar.style.cssText += `
    position: fixed !important;
    top: 0 !important;
    left: -10000px !important;
    width: ${sidebarWidth}px !important;
    height: auto !important;
    min-height: 0 !important;
    visibility: hidden !important;
    pointer-events: none !important;
    overflow: visible !important;
    `;

    const measurementDetails = Array.from(measurementSidebar.querySelectorAll('details'));
    measurementDetails.forEach((measurementDetail, index) => {
    const sourceDetail = sourceDetails[index];
    measurementDetail.open = targetOpenDetails === undefined
    ? sourceDetail.open
    : sourceDetail === targetOpenDetails;
    });

    document.body.appendChild(measurementSidebar);

    // 先一次性把所有 scrollHeight 读完，再统一写 CSS 变量。
    // 原写法在同一个循环里「读 scrollHeight → 写 style」交替进行：每次写都会让布局失效，
    // 下一次读就强制同步重排，4 个 details 就是 4 次重排。
    // 分批后读与写各自成组，一次测量只需要一次布局。写入的值与顺序完全不变。
    const l3MenuHeights = measurementDetails.map(measurementDetail => {
    const measurementL3Menu = measurementDetail.querySelector('.l3-menu-list');
    return measurementL3Menu ? measurementL3Menu.scrollHeight : null;
    });
    l3MenuHeights.forEach((height, index) => {
    if (height !== null) {
    measurementDetails[index].style.setProperty('--l3-menu-height', `${height}px`);
    }
    });

    const measurementMenu = measurementSidebar.querySelector('.l2-menu-list');
    const measurementAllProducts = measurementSidebar.querySelector('.mega-menu-sidebar__all-products');
    const menuHeight = measurementMenu ? measurementMenu.getBoundingClientRect().height : 0;
    const allProductsHeight = measurementAllProducts
    ? measurementAllProducts.getBoundingClientRect().height : 0;
    const allProductsStyle = measurementAllProducts
    ? window.getComputedStyle(measurementAllProducts) : null;
    const allProductsMargin = allProductsStyle
    ? parseFloat(allProductsStyle.marginTop || 0) + parseFloat(allProductsStyle.marginBottom || 0)
    : 0;
    measurementSidebar.remove();

    return Math.ceil(menuHeight + allProductsHeight + allProductsMargin);
    };

    const getContentHeight = () => {
    const activePane = this.querySelector('.mega-menu-content-pane.active');
    return activePane ? Math.ceil(activePane.scrollHeight) : 0;
    };

    const getPopupHeight = targetOpenDetails => {
    return Math.max(getSidebarHeight(targetOpenDetails), getContentHeight());
    };

    const syncSidebarHeight = () => {
    if (window.matchMedia('(min-width: 1068px)').matches) {
    this.style.height = `${getPopupHeight()}px`;
    } else {
    this.style.removeProperty('height');
    }
    };

    // Set the initial explicit height before the mega menu is first shown;
    // the following frame accounts for late font/layout settling.
    syncSidebarHeight();
    requestAnimationFrame(syncSidebarHeight);
    window.addEventListener('resize', syncSidebarHeight);

    // Direct L2 collection tabs change the product pane only on click.
    // L3 links remain hover-driven as part of the expanded L2 interaction.
    l2Tabs.forEach(tab => {
    tab.addEventListener('click', () => {
    this.deactivateAll(allL2Containers, l3Links);
    tab.classList.add('active');
    this.activatePane(tab.dataset.target);
    });
    });

    l2Details.forEach(details => {
    const summary = details.querySelector('.l2-summary');
    const l3Menu = details.querySelector('.l3-menu-list');
    if (l3Menu) {
    details.style.setProperty('--l3-menu-height', `${l3Menu.scrollHeight}px`);
    }
    if (summary && l3Menu) {
    summary.addEventListener('click', event => {
    if (!window.matchMedia('(min-width: 1068px)').matches) {
    return;
    }
    event.preventDefault();
    details.style.setProperty('--l3-menu-height', `${l3Menu.scrollHeight}px`);
    const currentHeight = getPopupHeight();
    this.style.height = `${currentHeight}px`;
    this.offsetHeight;

    if (details.open) {
    // The active group may be collapsed. The right product pane remains the
    // other height constraint while the left side closes.
    details.open = false;
    requestAnimationFrame(() => {
    this.animateHeight(currentHeight, getPopupHeight(null));
    });
    return;
    }

    // First collapse the previous group in a fixed-height container, then open
    // the requested group and let JS animate the outer height to its real size.
    suppressL3HoverUntilPointerMoves = true;
    openPointerPosition = lastPointerPosition && { ...lastPointerPosition };
    closeOtherL2Details(details);
    window.setTimeout(() => {
    details.open = true;
    activateFirstL3(summary);
    requestAnimationFrame(() => {
    this.animateHeight(currentHeight, getPopupHeight());
    });
    }, 90);
    });
    }
    });

    // L3 sub-tabs
    l3Links.forEach(link => {
    link.addEventListener('mouseover', () => {
    if (suppressL3HoverUntilPointerMoves) {
    return;
    }
    allL2Containers.forEach(el => el.classList.remove('active'));
    this.activateTab(link, l3Links);
    });
    });
    }

    /**
     * Animates the outer menu to a pre-calculated height before changing any
     * L2/L3 state. This deliberately keeps the old left and right content
     * visible until the height transition is complete.
     */
    animateHeight(startHeight, targetHeight, onComplete) {
    this.heightTransitionToken += 1;
    const token = this.heightTransitionToken;
    if (this.heightTransitionFrame) {
    cancelAnimationFrame(this.heightTransitionFrame);
    this.heightTransitionFrame = null;
    }

    this.classList.add('is-height-transitioning');
    this.style.height = `${startHeight}px`;
    this.offsetHeight;
    if (Math.abs(targetHeight - startHeight) < 1) {
    this.finishHeightTransition(token, onComplete);
    return;
    }

    const duration = 200;
    const startTime = performance.now();
    const easeOutCubic = progress => 1 - Math.pow(1 - progress, 3);

    const tick = now => {
    if (token !== this.heightTransitionToken) {
    return;
    }

    const progress = Math.min((now - startTime) / duration, 1);
    const height = startHeight + (targetHeight - startHeight) * easeOutCubic(progress);
    this.style.height = `${height}px`;

    if (progress < 1) {
    this.heightTransitionFrame = requestAnimationFrame(tick);
    return;
    }

    this.heightTransitionFrame = null;
    this.finishHeightTransition(token, onComplete);
    };

    this.heightTransitionFrame = requestAnimationFrame(tick);
    }

    finishHeightTransition(token, onComplete) {
    if (token !== this.heightTransitionToken) {
    return;
    }
    if (typeof onComplete === 'function') {
    onComplete();
    }
    this.classList.remove('is-height-transitioning');
    }

    /**
    * Activates a tab and its corresponding content pane.
    * @param {HTMLElement} tabElement - The tab element that was hovered.
    * @param {NodeListOf<Element>} siblingTabElements - A list of tabs to consider its siblings for deactivation.
        */
        activateTab(tabElement, siblingTabElements) {
        if (!tabElement.dataset.target || tabElement.classList.contains('active')) {
        return;
        }
        siblingTabElements.forEach(el => el.classList.remove('active'));
        tabElement.classList.add('active');
        this.activatePane(tabElement.dataset.target);
        }

        /**
        * Activates a single content pane by its ID.
        * @param {string} targetPaneId - The ID of the pane to activate (e.g., "#pane-123").
        */
        activatePane(targetPaneId) {
        this.contentPanes.forEach(pane => pane.classList.remove('active'));
        if (targetPaneId) {
        const targetPane = this.querySelector(targetPaneId);
        if (targetPane) {
        targetPane.classList.add('active');
        }
        }
        }

        /**
        * Deactivates all active tabs and summaries across the menu.
        */
        deactivateAll(l2s, l3s) {
        l2s.forEach(el => el.classList.remove('active'));
        l3s.forEach(el => el.classList.remove('active'));
        }
        }
        customElements.define('mega-menu-sidebar', MegaMenuSidebar);
        }
